import { Button, cn } from '@sea-snag/ui';
import { Building2, Loader2, MapPin, Star } from 'lucide-react';
import { useState } from 'react';

import { useConnect } from '../../hooks/use-setup';
import type { CompanyOption } from '../../lib/api-client';

interface SetupCompanyStepProps {
  orgPid: string;
  companies: CompanyOption[];
  apiKey: string;
  onConnected: () => void;
}

export function SetupCompanyStep({
  orgPid,
  companies,
  apiKey,
  onConnected,
}: SetupCompanyStepProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const connect = useConnect(orgPid);

  function handleConnect() {
    if (!selectedId) return;

    connect.mutate(
      { provider: 'mmk', apiKey, companyExternalId: selectedId },
      { onSuccess: () => onConnected() }
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="size-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">Select your company</h3>
        <p className="text-sm text-muted-foreground">
          Multiple companies were found. Choose the one you want to connect.
        </p>
      </div>

      <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
        {companies.map((company) => (
          <button
            key={company.id}
            type="button"
            onClick={() => setSelectedId(company.id)}
            className={cn(
              'flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors',
              selectedId === company.id
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border hover:border-primary/50'
            )}
          >
            <span className="font-medium">{company.name}</span>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {(company.city || company.country) && (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3" />
                  {[company.city, company.country].filter(Boolean).join(', ')}
                </span>
              )}
              {company.rating && (
                <span className="flex items-center gap-1">
                  <Star className="size-3" />
                  {company.rating.average.toFixed(1)} ({company.rating.reviews})
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      <Button onClick={handleConnect} disabled={!selectedId || connect.isPending}>
        {connect.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Connecting...
          </>
        ) : (
          'Connect'
        )}
      </Button>
    </div>
  );
}
